import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Site } from '@shared/types'
import { get } from '../../lib/api'
import { localDateKey, shiftDateKey } from '../../lib/format'
import { useSession } from '../../state/session'
import { Button, Field, SectionCard, Spinner } from '../../components/ui'

/**
 * Pick a site and a date range; the report opens as a plain page laid out for
 * paper, and the phone's or computer's own "Save as PDF" does the rest — no
 * PDF library shipped to every crew phone for an office-only feature.
 */
export default function AdminExport() {
  const { settings } = useSession()
  const navigate = useNavigate()
  const today = localDateKey(new Date(), settings.timezone)
  const [sites, setSites] = useState<Site[] | null>(null)
  const [siteId, setSiteId] = useState('')
  const [from, setFrom] = useState(shiftDateKey(today, -6))
  const [to, setTo] = useState(today)

  useEffect(() => {
    void get<{ sites: Site[] }>('/api/admin/sites')
      .then((res) => {
        setSites(res.sites)
        setSiteId((current) => current || res.sites.find((s) => s.active)?.id || '')
      })
      .catch(() => setSites([]))
  }, [])

  if (!sites) return <div className="py-16"><Spinner /></div>

  const valid = Boolean(siteId && from && to && from <= to)

  return (
    <SectionCard title="Site report">
      <p className="text-sm text-graphite-2">
        Every update on one site over a date range — photos, specs and change orders included — plus
        the site’s documents. It opens ready to print; choose <strong>Save as PDF</strong> in the
        print dialog.
      </p>
      <Field label="Site" required>
        <select className="field" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          {sites.map((site) => (
            <option key={site.id} value={site.id}>
              {site.name}{site.active ? '' : ' (archived)'}
            </option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="From">
          <input className="field font-mono" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="To">
          <input className="field font-mono" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </div>
      <Button
        variant="primary"
        size="lg"
        className="w-full"
        disabled={!valid}
        onClick={() => navigate(`/report?${new URLSearchParams({ siteId, from, to })}`)}
      >
        Open the report
      </Button>
    </SectionCard>
  )
}
